import { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useEffect, useRef, useState } from 'react'
import { LoadingLivestream } from '../components/LoadingLivestream'
import type { VideoWallTile } from './types'
import { useVideoTiles } from './useVideoTiles'
import { NoVideoTile, PreviewTile, VideoTile } from './VideoTile'

const TILE_TIMEOUT_MS = 10_000

const CONTAINER_BREAKPOINT = 850
const CONTAINER_QUERY = `@container (max-width: ${CONTAINER_BREAKPOINT}px)`
const CELLS_VERTICAL = 5
const PREVIEW_WIDTH = 170

export const VideoWall = ({ vehicleId }: { vehicleId: string }) => {
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const previewRefs = useRef<Array<HTMLCanvasElement | null>>([])
  const tiles = useVideoTiles()
  const [focusedTile, setFocusedTile] = useState(tiles.at(0))
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (tiles.length > 0 || timedOut) return
    const id = setTimeout(() => setTimedOut(true), TILE_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [tiles.length, timedOut])
  const { trackMetric } = useMetricTracking()

  const cameraSelectionStartTime = useRef<number | null>(null)
  const lastSelectedCamera = useRef<string | null>(null)

  const handleCameraSelection = async (
    tile: VideoWallTile,
    selectionType: 'manual' | 'automatic' = 'manual',
  ) => {
    if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
      const selectionDuration = Date.now() - cameraSelectionStartTime.current
      await trackMetric({
        metricName: 'CameraSelectionTime',
        value: selectionDuration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'cameraName', value: lastSelectedCamera.current },
          { name: 'vehicleId', value: vehicleId },
          { name: 'tileId', value: String(focusedTile?.id ?? -1) },
          { name: 'selectionType', value: selectionType },
        ],
        namespace: 'FleetControl/ad-operator-video',
      })
    }

    setFocusedTile(tile)
    lastSelectedCamera.current = tile.name
    cameraSelectionStartTime.current = Date.now()
  }

  const emptyCells = Math.max(0, CELLS_VERTICAL - tiles.length)

  useEffect(() => {
    const videos = videoRefs.current
    const previews = previewRefs.current
    if (!videos || !previews) return

    tiles.forEach(tile => {
      const video = videos[tile.id]
      const preview = previews[tile.id]
      if (!video || !preview) return

      const context = preview.getContext('2d')
      if (!context) return

      const width = video.videoWidth
      const height = video.videoHeight
      preview.width = width
      preview.height = height

      const drawPreview = () => {
        if (video.ended) return
        context.drawImage(video, 0, 0, width, height)
        requestAnimationFrame(drawPreview)
      }

      drawPreview()
    })
  }, [tiles])

  useEffect(() => {
    const trackCameraChange = (
      newTile: { id: number; name: string } | undefined,
    ) => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'selectionType', value: 'automatic' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }

      if (newTile) {
        lastSelectedCamera.current = newTile.name
        cameraSelectionStartTime.current = Date.now()
      } else {
        lastSelectedCamera.current = null
        cameraSelectionStartTime.current = null
      }
    }

    if (focusedTile && tiles.some(tile => tile.id === focusedTile.id)) {
      return
    }

    // focused tile set but not available anymore -> update or remove
    if (focusedTile && !tiles.some(tile => tile.id === focusedTile.id)) {
      const newTile = tiles.length > 0 ? tiles.at(0) : undefined
      trackCameraChange(newTile)
      setFocusedTile(newTile)
      return
    }

    // focused tile NOT set and tiles available -> set
    if (focusedTile === undefined && tiles.length > 0) {
      const newTile = tiles.at(0)
      trackCameraChange(newTile)
      setFocusedTile(newTile)
    }
  }, [tiles, focusedTile, trackMetric, vehicleId])

  useEffect(() => {
    return () => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'sessionEnd', value: '1' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }
    }
  }, [trackMetric, vehicleId])

  if (!focusedTile) {
    return timedOut ? (
      <NoVideoTile onRetry={() => setTimedOut(false)} />
    ) : (
      <LoadingLivestream />
    )
  }

  return (
    <div css={{ containerType: 'inline-size' }}>
      <div
        css={theme => ({
          display: 'flex',
          gap: theme.space.Space1,
          [CONTAINER_QUERY]: {
            flexDirection: 'column',
          },
        })}
      >
        {/* Focused video */}
        <div
          css={{
            flex: 1,
            minWidth: 0,
            minHeight: 300,
            display: 'grid',
            borderRadius: 8,
            overflow: 'hidden',
            backgroundColor: 'black',
            '& > *': {
              gridArea: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          {tiles.map(tile => (
            <div
              key={tile.id}
              css={{
                visibility: tile.id === focusedTile.id ? 'visible' : 'hidden',
              }}
            >
              <VideoTile
                ref={_ref => {
                  videoRefs.current[tile.id] = _ref
                }}
                tileId={tile.id}
                name={tile.name}
              />
            </div>
          ))}
        </div>

        {/* Previews */}
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space1,
            [CONTAINER_QUERY]: {
              flexDirection: 'row',
            },
          })}
        >
          {tiles.map(tile => {
            const isFocused = tile.id === focusedTile?.id
            return (
              <div
                key={`preview-${tile.id}`}
                css={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'black',
                  overflow: 'hidden',
                  borderRadius: 8,
                  width: PREVIEW_WIDTH,
                  aspectRatio: '16 / 10',
                  cursor: 'pointer',
                  boxShadow: isFocused
                    ? `inset 0 0 0 4px ${theme.semantic.ColorContentDefault}`
                    : 'none',
                  [CONTAINER_QUERY]: {
                    width: 0,
                    flex: 1,
                    minWidth: 0,
                  },
                })}
                onClick={() => handleCameraSelection(tile, 'manual')}
              >
                <PreviewTile
                  ref={_ref => {
                    previewRefs.current[tile.id] = _ref
                  }}
                  name={tile.name}
                  isFocused={isFocused}
                />
              </div>
            )
          })}

          {Array.from({ length: emptyCells }, (_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
              key={`empty-${i}`}
              css={theme => ({
                backgroundColor: theme.semantic.ColorSurfaceReduced,
                borderRadius: 8,
                width: PREVIEW_WIDTH,
                aspectRatio: '16 / 10',
                [CONTAINER_QUERY]: {
                  width: 0,
                  flex: 1,
                  minWidth: 0,
                },
              })}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
